import clsx from 'clsx'
import { type HTMLAttributes, type ReactNode, forwardRef } from 'react'

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Optional title rendered in a header band. */
  title?: ReactNode
  /** Optional actions rendered to the right of the title. */
  actions?: ReactNode
  /** Tighter padding (used in dense lists). */
  dense?: boolean
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { title, actions, dense, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={clsx(
        // SLP panel body (redesign V2): white surface on the gray page,
        // hairline border, 6px radius. NB: --bg is the PAGE background under
        // the SLP token remap, so the card surface must be --bg-2 (panel).
        'overflow-hidden rounded-[6px] border border-[var(--border)] bg-[var(--bg-2)] shadow-sm',
        className,
      )}
      {...rest}
    >
      {title ? (
        actions ? (
          // With actions: compact zebra header band (SLP sub-row voice) so
          // right-side buttons stay readable.
          <div className="flex min-h-[30px] items-center justify-between gap-2 border-b border-[var(--border)] bg-[var(--bg-3)] px-3 py-1">
            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-[var(--text-3)]">
              {title}
            </div>
            <div className="flex items-center gap-2">{actions}</div>
          </div>
        ) : (
          // Pure title: the SLP blue title bar, 26px, centered white text —
          // every titled card across the app reads as an SLP panel.
          <div className="flex h-[26px] items-center justify-center bg-[#29abe2] px-3 text-[12px] font-semibold leading-none text-white">
            {title}
          </div>
        )
      ) : null}
      <div className={dense ? 'p-3' : 'p-4'}>{children}</div>
    </div>
  )
})
